// ============================================================================
//  storage.js — the single seam from the prototype, now backed by Supabase.
//
//  The prototype kept all data in a {people, goals, marks} object held in
//  React state and persisted via loadState/saveState. We preserve that shape
//  so the UI is unchanged, but each mutation now writes through to Supabase.
//
//  RLS does the security filtering on read: a mentee sees only themselves,
//  a mentor sees their groups, an admin sees everyone.
// ============================================================================

import { supabase } from "./supabase.js";
import { compressForUpload } from "./compress.js";
import { isDemo } from "../demo/demoMode.js";
import * as demo from "../demo/demoStorage.js";

const BUCKET = "goal-photos";

// PostgREST truncates at max_rows (1000) without telling you. Anything that
// scales with men x goals x months has to be paged or a big cohort silently
// loses its oldest mile markers.
const PAGE = 1000;

async function fetchAll(table, columns, shape = (q) => q) {
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await shape(
      supabase.from(table).select(columns).range(from, from + PAGE - 1)
    );
    if (error) return { data: null, error };
    rows.push(...(data ?? []));
    if (!data || data.length < PAGE) break;
  }
  return { data: rows, error: null };
}

/* ---------- read ---------------------------------------------------------- */

export async function loadState(currentUserId) {
  if (isDemo()) return demo.loadState(currentUserId);
  // No application-level deleted_at filter any more. can_read_man() already
  // hides suspended teammates from peers and shows them to the leaders who
  // are entitled to unsuspend them; filtering here as well used to hide
  // suspended men from the very mentors who needed to restore them.
  //
  // PostgREST caps responses at max_rows (1000 in config.toml), so the tables
  // that grow with men x goals x months are paged explicitly.
  const [profilesRes, groupsRes, leadersRes, goalsRes, marksRes, photosRes] = await Promise.all([
    supabase.from("profiles").select("*"),
    supabase.from("groups").select("id, name, max_members").is("deleted_at", null),
    supabase.from("group_leaders").select("user_id, group_id"),
    supabase.from("goals").select("*").is("deleted_at", null).order("id"),
    fetchAll("mile_markers", "*", (q) => q.order("goal_id").order("month")),
    fetchAll("goal_photos", "*", (q) => q.order("goal_id")),
  ]);

  for (const r of [profilesRes, groupsRes, leadersRes, goalsRes, marksRes, photosRes]) {
    if (r.error) throw r.error;
  }

  const people = {};
  for (const p of profilesRes.data) {
    people[p.id] = {
      id: p.id,
      name: p.full_name,
      role: p.role,
      retreat: "Equilibrium Retreat",
      photo: p.photo_url ?? "",
      group_id: p.group_id,
      is_suspended: !!p.deleted_at,
    };
  }

  const groups = {};
  for (const g of groupsRes.data) {
    groups[g.id] = { id: g.id, name: g.name, max_members: g.max_members };
  }

  // groupId -> [userId], plus the flat list of teams the viewer himself leads.
  const leaders = {};
  const myLedGroupIds = [];
  for (const row of leadersRes.data) {
    if (!leaders[row.group_id]) leaders[row.group_id] = [];
    leaders[row.group_id].push(row.user_id);
    if (row.user_id === currentUserId) myLedGroupIds.push(row.group_id);
  }

  const goals = {};
  const goalIdToOwner = {};
  for (const g of goalsRes.data) {
    goalIdToOwner[g.id] = g.owner_id;
    if (!goals[g.owner_id]) goals[g.owner_id] = [];
    goals[g.owner_id].push({
      id: g.id,
      f: g.f,
      title: g.title,
      smart: { s: g.smart_s, m: g.smart_m, a: g.smart_a, r: g.smart_r, t: g.smart_t },
      note: g.note ?? "",
      private_details: !!g.private_details,
      photos: [], // photos hydrated below
    });
  }

  const marks = {};
  for (const m of marksRes.data) {
    if (!marks[m.goal_id]) marks[m.goal_id] = [];
    marks[m.goal_id].push({
      month: m.month,
      pct: m.pct,
      rag: m.rag,
      note: m.note ?? "",
      blockers: m.blockers ?? "",
      commitment: m.commitment ?? "",
      commitment_kept: m.commitment_kept,
      ts: new Date(m.created_at).getTime(),
    });
  }

  // Attach photo METADATA only — no signed URLs yet. Signing here meant one
  // sequential round trip per goal, which was tolerable when a man only ever
  // loaded his own goals. Now that teammates see each other, that fans out to
  // the whole roster on every page load. GoalCard calls signPhotoUrls() when
  // the gallery is actually opened instead, which also avoids handing out
  // 1-hour URLs that expire while a long roster sits open.
  const photosByGoal = {};
  for (const ph of photosRes.data) {
    if (!photosByGoal[ph.goal_id]) photosByGoal[ph.goal_id] = [];
    photosByGoal[ph.goal_id].push(ph);
  }
  for (const ownerId of Object.keys(goals)) {
    for (const g of goals[ownerId]) {
      g.photos = (photosByGoal[g.id] ?? []).map((p) => ({
        id: p.id,
        dataUrl: "",                       // filled in lazily by signPhotoUrls
        storage_path: p.storage_path,
        ts: new Date(p.taken_at).getTime(),
      }));
    }
  }

  return { people, goals, marks, groups, leaders, myLedGroupIds, currentUserId };
}

/**
 * Mint signed URLs for a batch of storage paths.
 *
 * Supabase caps createSignedUrls at a few hundred paths per call, so chunk it;
 * the chunks go out in parallel rather than one goal at a time.
 * Returns a { [storage_path]: signedUrl } map. Paths that fail to sign are
 * simply absent, so callers should fall back to "" rather than assume a hit.
 */
export async function signPhotoUrls(paths) {
  if (isDemo()) return demo.signPhotoUrls(paths);
  const unique = [...new Set((paths ?? []).filter(Boolean))];
  if (!unique.length) return {};

  const CHUNK = 100;
  const chunks = [];
  for (let i = 0; i < unique.length; i += CHUNK) chunks.push(unique.slice(i, i + CHUNK));

  const results = await Promise.all(
    chunks.map((c) => supabase.storage.from(BUCKET).createSignedUrls(c, 60 * 60))
  );

  const map = {};
  results.forEach(({ data }, ci) => {
    (data ?? []).forEach((row, i) => {
      // The API echoes `path`, but fall back to positional matching just in case.
      const key = row?.path ?? chunks[ci][i];
      if (key && row?.signedUrl) map[key] = row.signedUrl;
    });
  });
  return map;
}

/* ---------- profile ------------------------------------------------------- */

export async function updateProfile(userId, { name, photo }) {
  if (isDemo()) return demo.updateProfile(userId, { name, photo });
  const patch = { full_name: name };
  if (photo !== undefined) patch.photo_url = photo || null;
  const { error } = await supabase.from("profiles").update(patch).eq("id", userId);
  if (error) throw error;
}

/**
 * Record that a man has read the team-visibility notice.
 *
 * Stored on his profile rather than in localStorage: this is a consent notice,
 * so it has to survive a new phone and leave a record that he actually saw it.
 * Covered by the profiles_self_update policy — it is his own row.
 */
export async function acceptVisibilityNotice(userId) {
  if (isDemo()) return demo.acceptVisibilityNotice(userId);
  const { error } = await supabase
    .from("profiles")
    .update({ accepted_visibility_notice_at: new Date().toISOString() })
    .eq("id", userId);
  if (error) throw error;
}

/* ---------- goals --------------------------------------------------------- */

export async function upsertGoal(ownerId, goal) {
  if (isDemo()) return demo.upsertGoal(ownerId, goal);
  const row = {
    owner_id: ownerId,
    f: goal.f,
    title: goal.title,
    smart_s: goal.smart?.s ?? "",
    smart_m: goal.smart?.m ?? "",
    smart_a: goal.smart?.a ?? "",
    smart_r: goal.smart?.r ?? "",
    smart_t: goal.smart?.t ?? "",
    note: goal.note ?? "",
    // Hides this goal's monthly notes, blockers, commitments and photos from
    // teammates. Team Admins and above still see them. Enforced in RLS, not
    // here — this column is only the switch.
    private_details: !!goal.private_details,
  };
  if (goal.id) {
    const { data, error } = await supabase
      .from("goals").update(row).eq("id", goal.id).select().single();
    if (error) throw error;
    return data.id;
  }
  const { data, error } = await supabase.from("goals").insert(row).select().single();
  if (error) throw error;
  return data.id;
}

export async function deleteGoal(goalId) {
  if (isDemo()) return demo.deleteGoal(goalId);
  // soft-delete: keep history, hide from queries via the partial index
  const { error } = await supabase
    .from("goals").update({ deleted_at: new Date().toISOString() }).eq("id", goalId);
  if (error) throw error;
}

/* ---------- mile markers -------------------------------------------------- */

export async function upsertMark(goalId, mark) {
  if (isDemo()) return demo.upsertMark(goalId, mark);
  const row = {
    goal_id: goalId,
    month: mark.month,
    pct: mark.pct,
    rag: mark.rag,
    note: mark.note ?? "",
    blockers: mark.blockers ?? "",
    commitment: mark.commitment ?? "",
  };
  const { error } = await supabase
    .from("mile_markers").upsert(row, { onConflict: "goal_id,month" });
  if (error) throw error;
}

/* ---------- photos -------------------------------------------------------- */

export async function addPhoto(ownerId, goalId, file) {
  if (isDemo()) return demo.addPhoto(ownerId, goalId, file);
  const small = await compressForUpload(file);
  const path = `${ownerId}/${goalId}/${small.name}`;
  const up = await supabase.storage.from(BUCKET).upload(path, small, {
    cacheControl: "3600",
    upsert: false,
    contentType: "image/jpeg",
  });
  if (up.error) throw up.error;

  const ins = await supabase
    .from("goal_photos")
    .insert({ goal_id: goalId, owner_id: ownerId, storage_path: path })
    .select()
    .single();
  if (ins.error) throw ins.error;

  const { data: signed } = await supabase.storage
    .from(BUCKET).createSignedUrl(path, 60 * 60);

  return {
    id: ins.data.id,
    dataUrl: signed?.signedUrl ?? "",
    storage_path: path,
    ts: Date.now(),
  };
}

export async function deletePhoto(photoId, storagePath) {
  if (isDemo()) return demo.deletePhoto(photoId, storagePath);
  // delete the row first; if RLS denies we never touch the file.
  const del = await supabase.from("goal_photos").delete().eq("id", photoId);
  if (del.error) throw del.error;
  if (storagePath) await supabase.storage.from(BUCKET).remove([storagePath]);
}

/* ---------- admin: suspend / unsuspend ----------------------------------- */

export async function setProfileSuspended(targetId, isSuspended) {
  if (isDemo()) return demo.setProfileSuspended(targetId, isSuspended);
  const { error } = await supabase.rpc("admin_set_profile_deleted", {
    target_id: targetId,
    is_deleted: isSuspended,
  });
  if (error) throw error;
}

/* ---------- team + roster management (RPCs) ------------------------------ */
//
//  Every one of these is a security-definer function that re-checks rank and
//  scope server-side. The messages are written to be shown to a man verbatim,
//  so `rpc()` unwraps them rather than dressing them up.

async function rpc(name, args, demoFn) {
  if (isDemo()) return demoFn();
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw new Error(error.message || `${name} failed`);
  return data;
}

export function createTeam(name, maxMembers = 15) {
  return rpc("create_team", { p_name: name, p_max_members: maxMembers },
    () => demo.createTeam(name, maxMembers));
}

export function renameTeam(groupId, name) {
  return rpc("rename_team", { p_group_id: groupId, p_name: name },
    () => demo.renameTeam(groupId, name));
}

export function setTeamCapacity(groupId, maxMembers) {
  return rpc("set_team_capacity", { p_group_id: groupId, p_max_members: maxMembers },
    () => demo.setTeamCapacity(groupId, maxMembers));
}

export function assignLeader(userId, groupId) {
  return rpc("assign_leader", { p_user_id: userId, p_group_id: groupId },
    () => demo.assignLeader(userId, groupId));
}

export function unassignLeader(userId, groupId) {
  return rpc("unassign_leader", { p_user_id: userId, p_group_id: groupId },
    () => demo.unassignLeader(userId, groupId));
}

export function moveManToTeam(userId, groupId) {
  return rpc("move_man_to_team", { p_user_id: userId, p_group_id: groupId },
    () => demo.moveManToTeam(userId, groupId));
}

export function setManRole(userId, role) {
  return rpc("set_man_role", { p_user_id: userId, p_role: role },
    () => demo.setManRole(userId, role));
}

/* ---------- coach (Edge Function) ---------------------------------------- */

export async function askCoach({ goal, messages, videos }) {
  if (isDemo()) return demo.askCoach({ goal, messages, videos });
  const { data, error } = await supabase.functions.invoke("goal-coach", {
    body: { goal, messages, videos },
  });
  if (error) throw error;
  return data;
}

/* ---------- invite (Edge Function) --------------------------------------- */

export async function inviteMan({ email, full_name, group_id, role = "mentee" }) {
  if (isDemo()) return demo.inviteMan({ email, full_name, group_id, role });
  const { data, error } = await supabase.functions.invoke("invite-man", {
    body: { email, full_name, group_id, role },
  });
  // functions.invoke surfaces a generic FunctionsHttpError on non-2xx; the
  // useful message is in the JSON body, so prefer that when it is there.
  if (error) throw new Error(data?.error || error.message || "Invite failed.");
  if (data?.error) throw new Error(data.error);
  return data;
}

/**
 * "Send me a new link" from the login page. Callable without a session — the
 * men who need it cannot sign in by definition.
 *
 * Always resolves with the same generic message whether or not the address is
 * on the roster; the function is careful not to leak who is in the ministry,
 * and this wrapper must not undo that by surfacing a different error.
 */
export async function requestInviteResend(email) {
  const { data, error } = await supabase.functions.invoke("resend-invite", {
    body: { email },
  });
  if (error && !data?.message) {
    throw new Error("Could not reach the server. Check your connection and try again.");
  }
  return data;
}

/* ---------- account recovery, admin side (Edge Function) ----------------- */

async function manAccess(body) {
  const { data, error } = await supabase.functions.invoke("man-access", { body });
  // A non-2xx arrives as a generic FunctionsHttpError; the sentence worth
  // showing a leader is in the JSON body, so prefer it.
  if (error || data?.error) {
    throw new Error(data?.error || error?.message || "That did not work.");
  }
  return data;
}

/**
 * Who has actually accepted his invite. profiles rows exist from the moment an
 * invite is sent, so this is the only way the roster can tell a man who signed
 * in this morning from one who never opened a link at all.
 *
 * Returns a map of id -> { email, confirmed, invited_at, last_sign_in_at },
 * covering only the men the caller is allowed to manage.
 */
export async function loadAccessStatus() {
  if (isDemo()) return demo.loadAccessStatus();
  const data = await manAccess({ action: "status" });
  return Object.fromEntries((data?.men ?? []).map((m) => [m.id, m]));
}

/**
 * Re-send one man's invite, or a password reset if he is already confirmed.
 *
 * Unlike the login-page box, this reports what actually happened — a leader
 * asking about his own man is owed the truth, including a mailer rejection.
 * The vagueness on the unauthenticated path exists to protect the roster from
 * strangers, and there is no stranger here.
 */
export async function resendForMan(userId) {
  if (isDemo()) return demo.resendForMan(userId);
  return manAccess({ action: "resend", user_id: userId });
}

/**
 * Break glass: set a man's password directly so he can be got in face to face.
 * Admin and above only, and always recorded in access_events.
 */
export async function setTempPassword(userId, password) {
  if (isDemo()) return demo.setTempPassword(userId, password);
  return manAccess({ action: "set_password", user_id: userId, password });
}

/**
 * Invite a whole cohort. The function paces the sends itself (Supabase Auth
 * throttles invite email) and returns a per-row result, so a partial failure
 * is reported rather than thrown.
 */
export async function inviteMany({ people, group_id, role = "mentee" }) {
  if (isDemo()) return demo.inviteMany({ people, group_id, role });
  const { data, error } = await supabase.functions.invoke("invite-man", {
    body: { people, group_id, role },
  });
  if (error && !data?.results) throw new Error(data?.error || error.message || "Invite failed.");
  return data;
}
